import { FEDERATION_NAMES } from "./federation-flag.js";

/**
 * Names as a reader sees them — the ONE display boundary for people and
 * federations. The feeds are typed by hand at the board: "Mekonnen , Selemawit"
 * (a space before the comma), "Kosovo *" (an organiser's footnote marker),
 * "Turkiye" (diacritics dropped), or a bare FIDE code ("GAM"). The data is left
 * as served; only what is painted is tidied.
 */

/**
 * Repairs spacing and punctuation, then title-cases all-caps tokens (owner, 2026-09-26).
 * Two- and three-letter initials stay uppercase unless before the first comma or in a wholly
 * shouted name without commas. JR/SR suffixes are title-cased; II/III/IV always stay uppercase.
 * Letters are counted across the whole token, including hyphen/apostrophe parts. Mixed case,
 * single-letter tokens and dotted initials are left as served.
 */
export function personName(raw: string): string {
  const clean = raw
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .replace(/,(?=\S)/g, ", ")
    .trim();
  const comma = clean.indexOf(",");
  const allShouted = comma < 0 && !/\p{Ll}/u.test(clean);
  return clean.replace(/\S+/g, (token, offset: number) => {
    if (/\p{Ll}/u.test(token) || (token.match(/\p{Lu}/gu)?.length ?? 0) < 2) return token;
    const word = token.replace(/[,.;:]+$/u, "");
    if (/^(II|III|IV)$/u.test(word)) return token;
    const letters = token.match(/\p{L}/gu)?.length ?? 0;
    const shoutedSurname = (comma >= 0 && offset < comma) || allShouted;
    if (!/^(JR|SR)$/iu.test(word) && letters <= 3 && !shoutedSurname) return token;
    return token.toLowerCase().replace(/(^|[^\p{L}])(\p{L})/gu, (_m, before: string, letter: string) => before + letter.toUpperCase());
  });
}

/**
 * The spellings a folded name cannot restore by title case: diacritics and the
 * odd capital. Keyed like `FEDERATION_NAMES` (folded, lower case).
 */
const FEDERATION_SPELLING: Readonly<Record<string, string>> = {
  "turkiye": "Türkiye",
  "curacao": "Curaçao",
  "cote d'ivoire": "Côte d’Ivoire",
  "sao tome and principe": "São Tomé and Príncipe",
  "timor-leste": "Timor-Leste",
  "hong kong, china": "Hong Kong, China",
  // The long form wraps a narrow standings column onto three lines; people say "United States".
  "united states of america": "United States",
  "macau, china": "Macau, China",
};

/** Small words that stay lower case inside a country name. */
const MINOR_WORDS = new Set(["and", "of", "the", "da", "de"]);

function titleCase(folded: string): string {
  return folded.replace(/[a-z]+/g, (word, offset: number) =>
    offset > 0 && MINOR_WORDS.has(word) ? word : word.charAt(0).toUpperCase() + word.slice(1));
}

/** Where the table's first name for a code is the long form, the name people use. */
const SHORT_NAME_OF_CODE: Readonly<Record<string, string>> = {
  USA: "United States",
  BRU: "Brunei",
  COM: "Comoros",
};

/** FIDE code → the name a reader knows it by (the table's first name for it, unless shortened above). */
const NAME_OF_CODE: ReadonlyMap<string, string> = (() => {
  const byCode = new Map<string, string>(Object.entries(SHORT_NAME_OF_CODE));
  for (const [folded, [code]] of Object.entries(FEDERATION_NAMES)) {
    if (!byCode.has(code)) byCode.set(code, FEDERATION_SPELLING[folded] ?? titleCase(folded));
  }
  return byCode;
})();

/**
 * A federation or team as a reader should see it: "GAM" → "Gambia",
 * "Turkiye 2" → "Türkiye 2", "Kosovo *" → "Kosovo". Anything the table does not
 * know (a club, a league team) is returned tidied but otherwise as served.
 */
export function federationLabel(raw: string): string {
  const clean = raw.replace(/\s*\*+\s*$/, "").replace(/\s+/g, " ").trim();
  if (/^[A-Z]{3}$/.test(clean)) return NAME_OF_CODE.get(clean) ?? clean;
  const suffix = /^(.+?)( (?:\d{1,2}|[A-Za-z]))?$/.exec(clean);
  const base = suffix?.[1] ?? clean;
  const spelled = FEDERATION_SPELLING[base.toLowerCase()];
  return spelled === undefined ? clean : `${spelled}${suffix?.[2] ?? ""}`;
}
